import { Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { DisplayPlatformSetting } from '../../../../types';
import {
  deriveSettingOptionTranslationKey,
  getSettingConstraintEntries,
} from '../../../../utils';

const { Text } = Typography;

export interface SettingMetadataProps {
  setting: DisplayPlatformSetting;
}

export function SettingMetadata({ setting }: SettingMetadataProps) {
  const { t } = useTranslation('settings');
  const unit = setting.unit
    ? t(`units.${setting.unit}`, { defaultValue: setting.unit })
    : t('metadata.none');
  const defaultValue =
    typeof setting.defaultValue === 'boolean'
      ? t(`values.${String(setting.defaultValue)}`)
      : setting.valueType === 'ENUM'
        ? t(
            deriveSettingOptionTranslationKey(
              setting.key,
              String(setting.defaultValue),
            ),
            { defaultValue: String(setting.defaultValue) },
          )
        : String(setting.defaultValue);
  const constraints = getSettingConstraintEntries(setting.constraints);

  return (
    <dl className="platform-setting-metadata">
      <div>
        <dt>
          <Text type="secondary">{t('metadata.default')}</Text>
        </dt>
        <dd>{defaultValue}</dd>
      </div>
      <div>
        <dt>
          <Text type="secondary">{t('metadata.unit')}</Text>
        </dt>
        <dd>{unit}</dd>
      </div>
      <div>
        <dt>
          <Text type="secondary">{t('metadata.type')}</Text>
        </dt>
        <dd>{t(`types.${setting.valueType}`)}</dd>
      </div>
      <div>
        <dt>
          <Text type="secondary">{t('metadata.version')}</Text>
        </dt>
        <dd>{setting.version}</dd>
      </div>
      <div>
        <dt>
          <Text type="secondary">{t('metadata.constraints')}</Text>
        </dt>
        <dd>
          {constraints.length > 0 ? (
            <Space size={[4, 4]} wrap>
              {constraints.map((constraint) => (
                <Tag key={constraint.key}>
                  {t(`constraints.${constraint.key}`, {
                    defaultValue: constraint.key,
                  })}
                  {`: ${constraint.value}`}
                </Tag>
              ))}
            </Space>
          ) : (
            t('metadata.none')
          )}
        </dd>
      </div>
    </dl>
  );
}
